import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';
import { stockMovementSchema } from './inventory';

/* Stock counts / stock opname (P1 §5.4, ADR-0022). Quantities: base unit, scale 1000. */

export const STOCK_COUNT_STATUSES = [
  'planned',
  'in_progress',
  'recount',
  'submitted',
  'approved',
  'rejected',
  'posted',
  'cancelled',
] as const;
export const stockCountStatusSchema = z.enum(STOCK_COUNT_STATUSES);
export type StockCountStatus = z.infer<typeof stockCountStatusSchema>;

export const stockCountLineSchema = z.object({
  id: uuidSchema,
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  category: z.string().nullable(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  /** Null when hidden from the caller (blind count, not an approver). */
  expected_qty_scaled: z.number().int().nullable(),
  counted_qty_scaled: z.number().int().nullable(),
  counted_by: uuidSchema.nullable(),
  counted_by_name: z.string().nullable(),
  counted_at: isoTimestampSchema.nullable(),
  first_count_qty_scaled: z.number().int().nullable(),
  recount_required: z.boolean(),
  movements_after_cutoff_scaled: z.number().int().nullable(),
  variance_qty_scaled: z.number().int().nullable(),
  variance_value_minor: z.number().int().nullable(),
  within_tolerance: z.boolean().nullable(),
  movement_id: uuidSchema.nullable(),
  version: z.number().int(),
});
export type StockCountLine = z.infer<typeof stockCountLineSchema>;

export const stockCountSummarySchema = z.object({
  id: uuidSchema,
  count_number: z.string(),
  status: stockCountStatusSchema,
  location_id: uuidSchema,
  location_name: z.string(),
  count_type: z.enum(['full', 'cycle']),
  blind: z.boolean(),
  planned_date: z.iso.date().nullable(),
  category: z.string().nullable(),
  item_ids: z.array(uuidSchema).nullable(),
  tolerance_bps: z.number().int(),
  tolerance_value_minor: z.number().int().nullable(),
  currency: z.string(),
  cutoff_at: isoTimestampSchema.nullable(),
  round: z.number().int(),
  notes: z.string().nullable(),
  created_by_name: z.string().nullable(),
  started_by_name: z.string().nullable(),
  submitted_by_name: z.string().nullable(),
  submitted_at: isoTimestampSchema.nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: isoTimestampSchema.nullable(),
  decision_note: z.string().nullable(),
  posted_by_name: z.string().nullable(),
  posted_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  /** Hidden (null) for counters of a blind count until it is decided. */
  stats: z
    .object({
      lines: z.number().int(),
      counted: z.number().int(),
      within_tolerance: z.number().int(),
      recount_required: z.number().int(),
      /** within_tolerance ÷ counted lines (bps); exclusions: uncounted lines. */
      accuracy_bps: z.number().int().nullable(),
      variance_value_minor: z.number().int(),
    })
    .nullable(),
  /** Whether the caller sees expected quantities. */
  expected_visible: z.boolean(),
});
export type StockCountSummary = z.infer<typeof stockCountSummarySchema>;

export const stockCountSchema = stockCountSummarySchema.extend({
  lines: z.array(stockCountLineSchema),
});
export type StockCount = z.infer<typeof stockCountSchema>;

export const stockCountListResponseSchema = z.object({ items: z.array(stockCountSummarySchema) });
export const stockCountListQuerySchema = z.object({ status: stockCountStatusSchema.optional() });

export const createStockCountRequestSchema = z.object({
  location_id: uuidSchema,
  count_type: z.enum(['full', 'cycle']).default('full'),
  blind: z.boolean().default(true),
  planned_date: z.iso.date().nullable().default(null),
  /** Cycle counts: these items (and/or the category). */
  item_ids: z.array(uuidSchema).min(1).max(500).nullable().default(null),
  category: z.string().trim().min(1).max(60).nullable().default(null),
  tolerance_bps: z.number().int().min(0).max(10_000).default(500),
  tolerance_value_minor: z
    .number()
    .int()
    .min(0)
    .refine(Number.isSafeInteger)
    .nullable()
    .default(null),
  notes: z.string().trim().min(1).max(500).nullable().default(null),
});

export const stockCountActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});

export const stockCountEntriesRequestSchema = z.object({
  entries: z
    .array(
      z.object({
        line_id: uuidSchema,
        /** Base unit, scale 1000. */
        counted_qty_scaled: z.number().int().min(0).refine(Number.isSafeInteger),
        /**
         * The line version the counter started from. A line changed since (by another
         * device or an earlier sync) is refused instead of silently overwritten.
         */
        line_version: z.number().int().positive(),
      }),
    )
    .max(200)
    .default([]),
  /** Items or lots found that are not on the sheet (expected 0). */
  add: z
    .array(z.object({ item_id: uuidSchema, lot_id: uuidSchema.nullable().default(null) }))
    .max(50)
    .default([]),
});

export const stockCountRecountRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  line_ids: z.array(uuidSchema).min(1).max(500),
  note: z.string().trim().min(1).max(300).optional(),
});
export const stockCountDecisionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(1).max(300).optional(),
});

export const stockCountPostResultSchema = z.object({
  count: stockCountSchema,
  movements: z.array(stockMovementSchema),
});

// ---- Stock accuracy report (P1 §10)

export const countAccuracyQuerySchema = z.object({ from: z.iso.date(), to: z.iso.date() });
export const countAccuracyResponseSchema = z.object({
  currency: z.string(),
  from: z.iso.date(),
  to: z.iso.date(),
  definitions: z.record(z.string(), z.string()),
  counts: z.number().int(),
  lines_evaluated: z.number().int(),
  lines_within_tolerance: z.number().int(),
  /** within ÷ evaluated, bps; null when nothing was evaluated. */
  accuracy_bps: z.number().int().nullable(),
  /** Lines excluded from the denominator (never evaluated, e.g. counts cancelled mid-way). */
  excluded_lines: z.number().int(),
  variance_value_minor: z.number().int(),
  by_location: z.array(
    z.object({
      location_id: uuidSchema,
      location_name: z.string(),
      counts: z.number().int(),
      lines_evaluated: z.number().int(),
      accuracy_bps: z.number().int().nullable(),
      variance_value_minor: z.number().int(),
    }),
  ),
});
